// src/admin/components/RoleProtectedRoute.tsx
import React from 'react';
import { Box, Typography, Card, CardContent, Button } from '@mui/material';
import { Lock as LockIcon } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import {
  isPathAllowedForPermissions,
  getDefaultPathForPermissions,
  ALWAYS_ALLOWED_PATHS,
} from './rolePermissions';

interface RoleProtectedRouteProps {
  children: React.ReactNode;
  path: string;
}

const AccessDenied: React.FC<{ redirectTo: string }> = ({ redirectTo }) => (
  <Box
    sx={{
      p: 3,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '60vh',
    }}
  >
    <Card sx={{ maxWidth: 480, width: '100%' }}>
      <CardContent sx={{ textAlign: 'center', py: 6 }}>
        <LockIcon sx={{ fontSize: 64, color: 'warning.main', mb: 2, opacity: 0.8 }} />
        <Typography variant="h5" gutterBottom fontWeight={700}>
          Access Denied
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          You don't have permission to view this page.
        </Typography>
        <Button variant="contained" href={redirectTo}>
          Go to Dashboard
        </Button>
      </CardContent>
    </Card>
  </Box>
);

const RoleProtectedRoute: React.FC<RoleProtectedRouteProps> = ({ children, path }) => {
  const { userProfile, profileLoading, isSuperAdmin } = useAuth();

  // Still loading — let the app loader handle it
  if (profileLoading) return null;

  // ── Super-admin: bypass ALL permission checks — render immediately ─────────
  // This is the single, definitive super-admin gate. isSuperAdmin is set in
  // AuthContext by checking userProfile.role === 'super-admin'. When true,
  // every route is accessible without further checks.
  if (isSuperAdmin) {
    return <>{children}</>;
  }

  // Always-allowed paths (profile, settings) — skip permission check
  if (ALWAYS_ALLOWED_PATHS.includes(path)) {
    return <>{children}</>;
  }

  const permissions = userProfile?.permissions ?? [];
  const defaultPath = getDefaultPathForPermissions(permissions, isSuperAdmin);

  if (!isPathAllowedForPermissions(permissions, path, isSuperAdmin)) {
    return <AccessDenied redirectTo={defaultPath} />;
  }

  return <>{children}</>;
};

export default RoleProtectedRoute;


// // src/components/RoleProtectedRoute.tsx
// import React from 'react';
// import { Navigate, useLocation } from 'react-router-dom';
// import { Box, Typography, Card, CardContent, Button } from '@mui/material';
// import { Lock as LockIcon } from '@mui/icons-material';
// import { useAuth } from '../../contexts/AuthContext';
// import { UserRole } from '../pages/users/userService';
// import { isPathAllowedForRole, getDefaultPathForRole } from './rolePermissions';

// interface RoleProtectedRouteProps {
//   children: React.ReactNode;
//   path: string;
// }

// const AccessDenied: React.FC<{ redirectTo: string }> = ({ redirectTo }) => (
//   <Box sx={{ p: 3, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
//     <Card sx={{ maxWidth: 480, width: '100%' }}>
//       <CardContent sx={{ textAlign: 'center', py: 6 }}>
//         <LockIcon sx={{ fontSize: 64, color: 'warning.main', mb: 2, opacity: 0.8 }} />
//         <Typography variant="h5" gutterBottom fontWeight={700}>
//           Access Denied
//         </Typography>
//         <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
//           You don't have permission to view this page.
//         </Typography>
//         <Button variant="contained" href={redirectTo}>
//           Go to Dashboard
//         </Button>
//       </CardContent>
//     </Card>
//   </Box>
// );

// const RoleProtectedRoute: React.FC<RoleProtectedRouteProps> = ({ children, path }) => {
//   const { userProfile, profileLoading } = useAuth();

//   // Still loading — let the app loader handle it
//   if (profileLoading) return null;

//   const role = userProfile?.role as UserRole | undefined;
//   const defaultPath = getDefaultPathForRole(role);

//   if (!isPathAllowedForRole(role, path)) {
//     return <AccessDenied redirectTo={defaultPath} />;
//   }

//   return <>{children}</>;
// };

// export default RoleProtectedRoute;